import Vue, { VNode } from 'vue';

import { loadScript } from '@/Common/Runtime/host';
import { defaultStyleSheetsURLs, serverProps } from '@/Common/Environment/const';

import { i18n } from 'i18next';
import aps from '@/HostApp/store/ApsModules/ApsModule';
import hostServices from '@/HostApp/services';

import App from '@/HostApp/ui/App.vue';

const { designTokensModule, cssBundlerModule, spriteBundlerModule, i18nServiceModule, fontLoaderModule } = hostServices;

export class HostApp {
	public vm: Vue | null = null;
	public apsHost!: Promise<void | Array<Event>>;
	protected i18nLoaded: null | Promise<void | i18n> = null;

	constructor() {
		const { locale, bootstrapCSSPath } = serverProps;

		aps.hostServices = hostServices;

		this.i18nLoaded = i18nServiceModule.init(locale).then(() => {
			return i18nServiceModule.loadDictionary();
		});

		fontLoaderModule.loadAllFonts();
		spriteBundlerModule.prefetchSprites();
		cssBundlerModule.prefetchStyleSheets([bootstrapCSSPath]);

		this.apsHost = this.loadAps();

		aps.notifications.runService().catch((e) => {
			// eslint-disable-next-line no-console
			console.warn('cannot start notification service: ', e);
		});
	}

	protected async onBeforeStart(): Promise<CSSStyleSheet | void> {
		const styleSheetsToApply = serverProps._vueWorkspace ? defaultStyleSheetsURLs : [serverProps.bootstrapCSSPath];

		if (!serverProps.isDefaultSkin) {
			const [cssStyleSheet] = await Promise.all([
				cssBundlerModule.getCSSStyleSheetByUrl(serverProps.bootstrapCSSPath),
				cssBundlerModule.applyCSSBundles(document, styleSheetsToApply),
				spriteBundlerModule.attachSprite(document.body as HTMLBodyElement),
				this.i18nLoaded
			]);

			return cssStyleSheet;
		} else {
			await Promise.all([
				spriteBundlerModule.attachSprite(document.body as HTMLBodyElement),
				cssBundlerModule.applyCSSBundles(document, styleSheetsToApply),
				this.i18nLoaded
			]);

			return Promise.resolve();
		}
	}

	public async renderViewport(): Promise<void> {
		const bootstrapStyleSheet = await this.onBeforeStart();

		if (bootstrapStyleSheet) {
			designTokensModule.mergeVariablesWithTheme(bootstrapStyleSheet);
		}

		this.vm = new Vue({
			el: '#app',
			render: (h): VNode => h(App)
		});
	}

	public loadAps(): Promise<void | Array<Event>> {
		const apsSrc = serverProps.devMode
			? `/aps/2/ui/runtime/client/aps/aps.debug.js?`
			: `/aps/2/ui/runtime/client/aps/aps.js?apsUIRuntimeVersion=${serverProps.uiRuntimeVersion}`;

		const hostLayerSrc = serverProps.devMode
			? `/aps/2/ui/runtime/host/hostLayer.debug.js?`
			: `/aps/2/ui/runtime/host/hostLayer.js?apsUIRuntimeVersion=${serverProps.uiRuntimeVersion}`;

		return loadScript(apsSrc).then(() => {
			return loadScript(hostLayerSrc);
		});
	}
}

const hostApp = new HostApp();
export default hostApp;
